

import { useEffect, useState, useRef } from "react";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Spacing } from "@/components/Spacing/Spacing";
import { useTranslations } from "next-intl";
import { activateAccount } from "@/lib/api/handlers/AuthenticationHandling";
import { Link } from "react-router-dom";
import { IconCircleCheck, IconAlertCircle, IconLoader2 } from "@tabler/icons-react";

type ActivateContentProps = {
  token: string;
};

type ActivateState = "loading" | "success" | "expired" | "alreadyActive" | "invalid" | "needsNewSignup";

export const ActivateContent: React.FC<ActivateContentProps> = ({ token }) => {
  const t = useTranslations("activate");
  const tErrors = useTranslations("errors");
  const [state, setState] = useState<ActivateState>("loading");
  const activatedRef = useRef(false);

  useEffect(() => {
    if (activatedRef.current) return;
    activatedRef.current = true;

    async function activate() {
      const response = await activateAccount(token);

      if (
        response &&
        "isServerError" in response &&
        response.isServerError === true
      ) {
        const msg = response.message;
        if (msg === "tokenExpired" || msg === "dataExpired") {
          setState("expired");
        } else if (msg === "alreadyActive" || msg === "userAlreadyExists") {
          setState("alreadyActive");
        } else if (msg === "activationNeedsNewSignup") {
          // D11 (AVV plan Rev. 6.5, P6a.4): a registration from the old
          // company lane of the invite form, activated after the AVV release —
          // it carries no AVV acceptance. The token is used up; the person
          // registers again through the normal registration.
          setState("needsNewSignup");
        } else {
          setState("invalid");
        }
        return;
      }

      setState("success");
    }

    activate();
  }, [token]);

  if (state === "loading") {
    return (
      <div className="flex flex-col items-center w-full" data-testid="activate-state-loading">
        <IconLoader2 size={48} stroke={1.5} className="font-primary animate-spin" />
        <Spacing height="h-4" />
        <p className="body-1-regular text-center">{t("loading")}</p>
      </div>
    );
  }

  if (state === "success" || state === "alreadyActive") {
    return (
      <div className="flex flex-col items-center w-full" data-testid={`activate-state-${state}`}>
        <IconCircleCheck size={48} stroke={1.5} className="font-success" />
        <Spacing height="h-4" />
        <p className="body-1-regular text-center">
          {state === "success" ? t("description") : t("errorAlreadyActive")}
        </p>
        <Spacing height="h-6" />
        <Link to="/auth/login" className="w-full">
          <PrimaryButton size="small" text={t("goToLogin")} />
        </Link>
      </div>
    );
  }

  // expired, invalid, or a new signup is needed (D11)
  return (
    <div className="flex flex-col items-center w-full" data-testid={`activate-state-${state}`}>
      <IconAlertCircle size={48} stroke={1.5} className="font-error" />
      <Spacing height="h-4" />
      <p className="body-1-regular text-center">
        {state === "expired"
          ? t("errorExpired")
          : state === "needsNewSignup"
            ? tErrors("activationNeedsNewSignup")
            : t("errorInvalid")}
      </p>
      <Spacing height="h-6" />
      <Link to="/auth/register" className="w-full">
        <PrimaryButton size="small" text={t("registerAgain")} />
      </Link>
      <Spacing height="h-3" />
      <Link to="/auth/login" className="w-full">
        <SecondaryButton size="small" text={t("goToLogin")} />
      </Link>
    </div>
  );
};
